// project import
import { OpenChosenVersion } from '~/beta/OpenChosenVersion';
import Loadable from '~/components/LoadableErrorReload';
import AdminImpactGroupsPage from '~/features/impact-groups/admin-impact-groups-page/AdminImpactGroupsPage';
import DashboardLayout from '~/layout/Dashboard';
import PagesLayout from '~/layout/Pages';
import { clearAccountsSelection } from '~/pages/dashboard/admin/accounts/search/accountsPageStore';
import PayoutDetailsRoute from '~/pages/dashboard/admin/payouts/PayoutDetailsRoute';
import { clearUsersSelection } from '~/pages/dashboard/admin/users/search/usersPageStore';
import { createAdminUserDetailRoutes } from '~/pages/dashboard/admin/users/user-detail/routing/routes';
import { queryClient } from '~/utils/query-client';
import { PagePolicies } from '~/utils/roles';

import { purchaseOrdersRoute } from './purchaseOrdersRoute';
import { restrictPage } from './restrictPage';
import { traderProfilesModerationRoute } from './traderProfilesModerationRoute';

// default layout
const DashboardDefault = Loadable(() => import('~/pages/dashboard/default'));

// end user pages (reused by admin)
const ProfileDetails = Loadable(() => import('~/pages/dashboard/profile/detailsTab'));

// admin pages
const AdminTraderDashboard = Loadable(() => import('~/pages/dashboard/admin/trader-dashboard'));
const AdminCouponDetails = Loadable(() => import('~/pages/dashboard/admin/coupons/CouponDetails'));
const AdminCouponsCrystal = Loadable(() => import('~/pages/dashboard/admin/coupons/index.crystal'));
const AdminPayouts = Loadable(() => import('~/pages/dashboard/admin/payouts'));
const AdminAffiliates = Loadable(() => import('~/pages/dashboard/admin/affiliates'));
const AdminAffiliateDetail = Loadable(
  () => import('~/pages/dashboard/admin/affiliates/AffiliateDetail'),
);
const AdminUserDetailRouterEntry = Loadable(
  () => import('~/pages/dashboard/admin/users/user-detail/routing/router.entry'),
);
const AdminKycCrystal = Loadable(() => import('~/pages/dashboard/admin/kyc/index.crystal'));
const AdminKycOfac = Loadable(() => import('~/pages/dashboard/admin/kyc/ofac/index.crystal'));
const AdminKycFraudReviewSettings = Loadable(
  () => import('~/pages/dashboard/admin/kyc/fraud-review-settings'),
);
const AdminKycComplianceReviewSettings = Loadable(
  () => import('~/pages/dashboard/admin/kyc/ofac/compliance-review-settings'),
);
const AdminLogCrystal = Loadable(() => import('~/pages/dashboard/admin/log/index.crystal'));
const AdminOrganization = Loadable(() => import('~/pages/dashboard/admin/organization'));
const AdminAccountsCrystal = Loadable(
  () => import('~/pages/dashboard/admin/accounts/index.crystal'),
);
const AdminAccountDetail = Loadable(
  () => import('~/pages/dashboard/admin/accounts/components/details'),
);
const AdminDateDetails = Loadable(
  () => import('~/pages/dashboard/admin/accounts/components/dateDetails'),
);

// All admin user detail sub-routes are now handled through the AdminUserDetailRouterEntry
// and routes are defined in the user-detail routing system

const AdminTransactionsCrystal = Loadable(
  () => import('~/pages/dashboard/admin/transactions/index.crystal'),
);
const AdminTradingProductFeesCrystal = Loadable(
  () => import('~/pages/dashboard/admin/trading-product-fees/index.crystal'),
);
const AdminPurchases = Loadable(() => import('~/pages/dashboard/admin/purchases/index.crystal'));
const AdminCheckoutSessionInsights = Loadable(
  () => import('~/pages/dashboard/admin/insights/checkout-session/index.crystal'),
);
const AdminInvestigations = Loadable(() => import('~/pages/dashboard/admin/cases/investigations'));
const AdminCreateInvestigation = Loadable(
  () => import('~/pages/dashboard/admin/cases/investigations/CreateInvestigationPage'),
);
const AdminCaseDetail = Loadable(
  () => import('~/pages/dashboard/admin/cases/investigations/CaseDetail'),
);
const AdminCaseSettings = Loadable(() => import('~/pages/dashboard/admin/cases/settings'));
const AdminUsersCrystal = Loadable(() => import('~/pages/dashboard/admin/users/index.crystal'));
const AdminBans = Loadable(() => import('~/pages/dashboard/admin/cases/bans'));
const AdminAccountsV2 = Loadable(() => import('~/pages/dashboard/admin/accounts/search'));
const AdminUsersV2 = Loadable(() => import('~/pages/dashboard/admin/users/search'));
const AdminBulkActionHistory = Loadable(
  () => import('~/pages/dashboard/admin/bulk-actions/history'),
);
const AdminBulkActionDetail = Loadable(() => import('~/pages/dashboard/admin/bulk-actions/detail'));
const AdminCreateBan = Loadable(() => import('~/pages/dashboard/admin/cases/bans/CreateBanPage'));
const AdminBanDetail = Loadable(() => import('~/pages/dashboard/admin/cases/bans/BanDetail'));
const AdminTraderProfiles = Loadable(() => import('~/pages/dashboard/admin/trader-profiles'));
const AdminValidateHandle = Loadable(
  () => import('~/pages/dashboard/admin/trader-profiles/validate'),
);

const AdminPayoutBatches = Loadable(() => import('~/pages/dashboard/admin/payouts/batches'));
const BatchAutomationSettings = Loadable(
  () => import('~/pages/dashboard/admin/payouts/batches/BatchAutomationSettings'),
);

// public pages
const ConfirmEmail = Loadable(() => import('~/pages/public/confirmEmail'));
const AgreementPage = Loadable(() => import('~/pages/public/agreement'));
const ErrorPage = Loadable(() => import('~/pages/maintenance/404'));
const AdminDashboardError = Loadable(() => import('~/components/AdminDashboardErrorBoundary'));

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: <DashboardLayout />,
      children: [
        {
          path: 'dashboard',
          children: [
            {
              path: 'default',
              element: <DashboardDefault />,
            },
            {
              path: 'admin',
              errorElement: <AdminDashboardError />,
              children: [
                {
                  path: 'profile/details',
                  element: restrictPage(<ProfileDetails />, PagePolicies.AdminProfilePage),
                },
                {
                  path: 'users',
                  element: restrictPage(
                    <OpenChosenVersion page="users">
                      <AdminUsersCrystal />
                    </OpenChosenVersion>,
                    PagePolicies.AdminUsersPage,
                  ),
                },
                {
                  path: 'users-v2',
                  loader: () => {
                    clearUsersSelection();
                    return null;
                  },
                  element: restrictPage(
                    <AdminUsersV2 />,
                    PagePolicies.BetaPages,
                    PagePolicies.AdminUsersPage,
                  ),
                },
                {
                  path: 'bulk-actions',
                  element: restrictPage(
                    <AdminBulkActionHistory />,
                    PagePolicies.BetaPages,
                    PagePolicies.AdminBulkActionsPage,
                  ),
                },
                {
                  path: 'bulk-actions/:workflowId',
                  element: restrictPage(
                    <AdminBulkActionDetail />,
                    PagePolicies.BetaPages,
                    PagePolicies.AdminBulkActionsPage,
                  ),
                },
                {
                  path: 'trader/:id/dashboard',
                  element: restrictPage(<AdminTraderDashboard />, PagePolicies.AdminAccountsPage),
                },
                {
                  ...createAdminUserDetailRoutes(queryClient)[0],
                  path: 'users/:id/*',
                  element: restrictPage(
                    <AdminUserDetailRouterEntry />,
                    PagePolicies.AdminUsersPage,
                  ),
                  errorElement: restrictPage(
                    createAdminUserDetailRoutes(queryClient)[0].errorElement,
                    PagePolicies.AdminUsersPage,
                  ),
                },
                {
                  path: 'accounts-v2',
                  loader: () => {
                    clearAccountsSelection();
                    return null;
                  },
                  element: restrictPage(
                    <AdminAccountsV2 />,
                    PagePolicies.BetaPages,
                    PagePolicies.AdminAccountsPage,
                  ),
                },
                {
                  path: 'accounts',
                  element: restrictPage(
                    <OpenChosenVersion page="accounts">
                      <AdminAccountsCrystal />
                    </OpenChosenVersion>,
                    PagePolicies.AdminAccountsPage,
                  ),
                },
                {
                  path: 'idv',
                  element: restrictPage(<AdminKycCrystal />, PagePolicies.AdminUsersPage),
                },
                {
                  path: 'idv/ofac',
                  element: restrictPage(<AdminKycOfac />, PagePolicies.AdminOfacPepSanctionsPage),
                },
                {
                  path: 'idv/fraud-review-settings',
                  element: restrictPage(
                    <AdminKycFraudReviewSettings />,
                    PagePolicies.KycFraudReviewSettingsPage,
                  ),
                },
                {
                  path: 'idv/ofac/compliance-review-settings',
                  element: restrictPage(
                    <AdminKycComplianceReviewSettings />,
                    PagePolicies.KycComplianceReviewSettingsPage,
                  ),
                },
                {
                  path: 'accounts/:id',
                  element: restrictPage(<AdminAccountDetail />, PagePolicies.AdminAccountsPage),
                },
                {
                  path: 'accounts/:id/trades/day/:date',
                  element: restrictPage(<AdminDateDetails />, PagePolicies.AdminAccountsPage),
                },
                {
                  path: 'coupons',
                  element: restrictPage(<AdminCouponsCrystal />, PagePolicies.AdminCouponsPage),
                },
                {
                  path: 'coupons/:id',
                  element: restrictPage(<AdminCouponDetails />, PagePolicies.AdminCouponsPage),
                },
                {
                  path: 'payouts',
                  element: restrictPage(<AdminPayouts />, PagePolicies.AdminPayoutsPage),
                },
                {
                  path: 'payouts/batches',
                  element: restrictPage(<AdminPayoutBatches />, PagePolicies.AdminPayoutsPage),
                },
                {
                  path: 'payouts/batches/settings',
                  element: restrictPage(<BatchAutomationSettings />, PagePolicies.AdminPayoutsPage),
                },
                {
                  path: 'payouts/:id',
                  element: restrictPage(<PayoutDetailsRoute />, PagePolicies.AdminPayoutsPage),
                },
                {
                  path: 'affiliates',
                  element: restrictPage(<AdminAffiliates />, PagePolicies.AdminAffiliatesPage),
                },
                {
                  path: 'affiliates/:id',
                  element: restrictPage(<AdminAffiliateDetail />, PagePolicies.AdminAffiliatesPage),
                },
                {
                  path: 'log',
                  element: restrictPage(<AdminLogCrystal />, PagePolicies.AdminLogPage),
                },
                {
                  path: 'organization',
                  element: restrictPage(<AdminOrganization />, PagePolicies.AdminOrganizationPage),
                },
                {
                  path: 'transactions',
                  element: restrictPage(
                    <AdminTransactionsCrystal />,
                    PagePolicies.AdminTransactionsPage,
                  ),
                },
                purchaseOrdersRoute,
                {
                  path: 'purchases',
                  element: restrictPage(<AdminPurchases />, PagePolicies.AdminTransactionsPage),
                },
                {
                  path: 'insights/checkout-session',
                  element: restrictPage(
                    <AdminCheckoutSessionInsights />,
                    PagePolicies.AdminCheckoutSessionInsightsPage,
                  ),
                },
                {
                  path: 'trading-product-fees',
                  element: restrictPage(
                    <AdminTradingProductFeesCrystal />,
                    PagePolicies.AdminProductsPage,
                  ),
                },
                {
                  path: 'impact-groups',
                  element: restrictPage(
                    <AdminImpactGroupsPage />,
                    PagePolicies.AdminImpactGroupsPage,
                  ),
                },
                {
                  path: 'trader-profiles/search',
                  element: restrictPage(
                    <AdminTraderProfiles />,
                    PagePolicies.AdminTraderProfilesPage,
                  ),
                },
                traderProfilesModerationRoute,
                {
                  path: 'trader-profiles/validate',
                  element: restrictPage(
                    <AdminValidateHandle />,
                    PagePolicies.AdminValidateHandlePage,
                  ),
                },
                {
                  path: 'cases/investigations',
                  element: restrictPage(
                    <AdminInvestigations />,
                    PagePolicies.AdminCaseInvestigationsPage,
                  ),
                },
                {
                  path: 'cases/investigations/new',
                  element: restrictPage(
                    <AdminCreateInvestigation />,
                    PagePolicies.AdminCaseInvestigationsPage,
                    PagePolicies.AdminCreateInvestigationPage,
                  ),
                },
                {
                  path: 'cases/investigations/:id',
                  element: restrictPage(
                    <AdminCaseDetail />,
                    PagePolicies.AdminCaseInvestigationsPage,
                  ),
                },
                {
                  path: 'cases/bans',
                  element: restrictPage(<AdminBans />, PagePolicies.AdminCaseBansPage),
                },
                {
                  path: 'cases/bans/new',
                  element: restrictPage(
                    <AdminCreateBan />,
                    PagePolicies.AdminCaseBansPage,
                    PagePolicies.AdminCreateBanPage,
                  ),
                },
                {
                  path: 'cases/bans/:id',
                  element: restrictPage(<AdminBanDetail />, PagePolicies.AdminCaseBansPage),
                },
                {
                  path: 'cases/settings',
                  element: restrictPage(<AdminCaseSettings />, PagePolicies.AdminCaseSettingsPage),
                },
              ],
            },
            {
              path: '*',
              element: <ErrorPage />,
            },
          ],
        },
      ],
    },
    {
      path: '/public',
      element: <PagesLayout />,
      children: [
        {
          path: 'confirm-email/:code',
          element: <ConfirmEmail />,
        },
        {
          path: 'agreement/:productType',
          element: <AgreementPage />,
        },
        {
          path: '*',
          element: <ErrorPage />,
        },
      ],
    },
  ],
};

export default MainRoutes;
